import { useMemo, useState } from "react";
import { TaxLayout } from "../../components/tax/TaxLayout.js";
import {
  Card,
  NumberField,
  StatCard,
} from "../../components/design-system/index.js";
import { seppCalculator } from "../../lib/util/sepp.js";
import { usePreferences } from "../../lib/preferences.js";

function formatCents(cents: number, hide: boolean): string {
  if (hide) return "••••";
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function TaxSeppPage() {
  const { privacyMode } = usePreferences();
  const hide = privacyMode === true;

  const [balance, setBalance] = useState<number>(1_000_000_00);
  const [age, setAge] = useState<number>(50);
  const [rate, setRate] = useState<number>(5);

  const rmd = useMemo(
    () =>
      seppCalculator({
        account_balance_cents: balance,
        current_age: age,
        method: "rmd",
      }),
    [balance, age],
  );
  const amort = useMemo(
    () =>
      seppCalculator({
        account_balance_cents: balance,
        current_age: age,
        method: "amortization",
        interest_rate_pct: rate,
      }),
    [balance, age, rate],
  );
  const annuity = useMemo(
    () =>
      seppCalculator({
        account_balance_cents: balance,
        current_age: age,
        method: "annuitization",
        interest_rate_pct: rate,
      }),
    [balance, age, rate],
  );

  return (
    <TaxLayout
      title="Rule 72(t) / SEPP calculator"
      subtitle="Pre-59½ retirement-account access without the 10% penalty. Three IRS-approved methods (Notice 2022-6)."
    >
      <Card className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <NumberField
          label="Account balance (cents)"
          value={balance}
          min={1}
          step={10_000_00}
          onChange={(e) => setBalance(Number(e.target.value))}
        />
        <NumberField
          label="Current age (30-59)"
          value={age}
          min={30}
          max={59}
          onChange={(e) => setAge(Number(e.target.value))}
        />
        <NumberField
          label="Interest rate (%)"
          value={rate}
          min={0.1}
          max={20}
          step={0.1}
          onChange={(e) => setRate(Number(e.target.value))}
          helper="Capped at the IRS max rate per Notice 2022-6 (greater of 5% or 120% of federal mid-term)."
        />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="text-sm font-semibold mb-2 text-[var(--color-text)]">
            Required Minimum Distribution
          </div>
          <div className="text-xs text-[var(--color-muted)] mb-3">
            Recomputed each year from the IRS Single Life Expectancy table.
          </div>
          <StatCard
            label="Year-1 distribution"
            value={formatCents(rmd.annual_distribution_cents, hide)}
            sub={`${rmd.effective_rate_pct.toFixed(2)}% of balance`}
          />
          <div className="mt-3 text-xs text-[var(--color-muted)]">
            Min years: {rmd.minimum_years} · Life expectancy: {rmd.life_expectancy_years}y
          </div>
        </Card>

        <Card className="p-5 border border-[var(--color-accent)]">
          <div className="text-sm font-semibold mb-2 text-[var(--color-text)]">
            Fixed Amortization
          </div>
          <div className="text-xs text-[var(--color-muted)] mb-3">
            Level payment over the life-expectancy years at the chosen rate.
          </div>
          <StatCard
            label="Annual distribution"
            value={formatCents(amort.annual_distribution_cents, hide)}
            sub={`${amort.effective_rate_pct.toFixed(2)}% of balance`}
          />
          <div className="mt-3 text-xs text-[var(--color-muted)]">
            Min years: {amort.minimum_years} · Total: {formatCents(amort.minimum_total_distributions_cents, hide)}
          </div>
        </Card>

        <Card className="p-5">
          <div className="text-sm font-semibold mb-2 text-[var(--color-text)]">
            Fixed Annuitization
          </div>
          <div className="text-xs text-[var(--color-muted)] mb-3">
            Annuity-factor formula. v1 uses Single Life Expectancy as N.
          </div>
          <StatCard
            label="Annual distribution"
            value={formatCents(annuity.annual_distribution_cents, hide)}
            sub={`${annuity.effective_rate_pct.toFixed(2)}% of balance`}
          />
          <div className="mt-3 text-xs text-[var(--color-muted)]">
            Min years: {annuity.minimum_years} · Total: {formatCents(annuity.minimum_total_distributions_cents, hide)}
          </div>
        </Card>
      </div>

      <Card className="p-4 text-xs text-[var(--color-muted)]">
        Bust the SEPP and the IRS retroactively imposes the 10% penalty on every prior distribution. Plan to keep payments going at least {Math.max(rmd.minimum_years, 5)} years (whichever is later: 5 years OR until age 59½).
      </Card>
    </TaxLayout>
  );
}
